import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'

import { AngularReactModule } from '@bubblydoo/angular-react'
import { Store } from '@ngrx/store'
import { clearNode, createDomain, sample, withRegion } from 'effector'

import { Observable, Subject, combineLatest, map, noop } from 'rxjs'
import { filter, takeUntil, tap } from 'rxjs/operators'

import { FlxSpinnerComponent } from '@flowx/angular-ui-toolkit'
import { CanvasData } from '@flowx/designer/modules/bpmn-editor/bpmn-canvas.types'
import { BpmnMouseEvent } from '@flowx/designer/modules/bpmn-editor/bpmn-editor.types'
import { BpmnGraphConstants } from '@flowx/designer/modules/bpmn-editor/bpmn-graph.constants'
import { BpmnEditorNodeActionsComponent } from '@flowx/designer/modules/bpmn-editor/components/bpmn-editor-node-actions/bpmn-editor-node-actions.component'
import { BpmnInstanceActionsStore } from '@flowx/designer/modules/bpmn-editor/components/bpmn-editor-node-actions/store/bpmn-instance-actions.store'
import { BPMNNodeActionsEffects } from '@flowx/designer/modules/bpmn-editor/components/bpmn-editor-node-actions/store/bpmn-node-actions.effects'
import { BPMNNodeActionsEvents } from '@flowx/designer/modules/bpmn-editor/components/bpmn-editor-node-actions/store/bpmn-node-actions.events'
import BpmnDiagram from '@flowx/designer/modules/bpmn-editor/react-flow/react/BpmnDiagram'
import { BpmnDiagramProps } from '@flowx/designer/modules/bpmn-editor/react-flow/react/bpmn-diagram.types'
import { BpmnEditorEvents } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.events'
import { BpmnEditorSamples } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.samples'
import {
  NodeDefinition,
  NodeDefinitionNormalized,
  ProcessDefinition,
} from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ProcessInstanceDetailsPanelComponent } from '@flowx/designer/modules/process/components/process-instance-details-panel/process-instance-details-panel.component'
import { AppEvents } from '@flowx/designer/shared/effector/app/app.events'
import { BpmnBoundaryEventEvents } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-boundary-event.events'
import { BpmnEditorStores } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-editor.stores'
import { BpmnNodeEvents } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-node.events'
import { ProcessEffector } from '@flowx/designer/shared/effector/process.effector'
import { BoundaryEvent } from '@flowx/designer/shared/entities/boundary-event/boundary-event.types'
import {
  ApplicationProcessData,
  ProcessInstanceBreadcrumb,
} from '@flowx/designer/shared/entities/process-instance.types'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'
import { selectQueryParams } from '@flowx/designer/shared/store/selectors/router.selectors'

import { buildInstanceCanvasData } from './instance-audit.util'

const EMPTY_CANVAS_DATA: CanvasData = {
  swimlanes: [],
  nodes: [],
  sequences: [],
  boundaryEvents: [],
}

@Component({
  selector: 'app-process-instance-react-flow',
  templateUrl: './process-instance-react-flow.component.html',
  styleUrls: ['./process-instance-react-flow.component.scss'],
  providers: [BpmnInstanceActionsStore],
  imports: [
    CommonModule,
    AngularReactModule,
    BpmnEditorNodeActionsComponent,
    FlxSpinnerComponent,
    ProcessInstanceDetailsPanelComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class ProcessInstanceReactFlowComponent implements OnInit, OnDestroy {
  #destroy$ = new Subject<void>()
  #destroyRef = inject(DestroyRef)
  #embedId: string
  #embedContext: string
  #store = inject(Store)
  #processInstanceService = inject(ProcessInstanceService)

  // Registers the effector samples that ProcessEffector.loadProcess relies on
  // (SequenceStores wiring etc.) — matches ProcessInstanceComponent's inject.
  // noinspection JSUnusedLocalSymbols
  readonly #bpmnEditorSamples = inject(BpmnEditorSamples)

  readonly #Effector = createDomain('ProcessInstanceReactFlowComponent')

  // Last zoom reported to the effector stores — pans re-use it, mirroring the
  // RF editor's onViewportChange handler.
  #lastReportedZoom: number | null = null

  #canvasData = signal<CanvasData>(EMPTY_CANVAS_DATA)

  // Instance behind the canvas data above. The canvas fits the diagram whenever
  // this changes: the viewer mounts it with EMPTY_CANVAS_DATA while the instance
  // loads, and keeps the same canvas when the user drills into a subprocess.
  #canvasInstanceUuid = signal<string | null>(null)

  // Observable view of the applicationProcessData signal input — drives the
  // effector load pipeline in ngOnInit. Assigned in the constructor so it reads
  // the input after its field initializer has run.
  #applicationProcessData$: Observable<ApplicationProcessData>

  applicationProcessData = input<ApplicationProcessData>(null)
  loading = input(false)
  definitionsExist = input(false)
  showBreadcrumb = input(true)

  navigateToSubProcess = output<{
    subprocessUuid: string
    name: string
    uuid: string
    previousEmbedId?: string
    previousEmbedContext?: string
    breadcrumbs: ProcessInstanceBreadcrumb[]
  }>()
  backToParentProcess = output<{ breadcrumbs: ProcessInstanceBreadcrumb[] }>()
  navigateToEmbeddedProcess = output<{
    // node.id is numeric; the untyped EventEmitter this replaced always emitted
    // it as-is, so keep the number to preserve the runtime payload exactly.
    embedId: number
    context: string
    previousEmbedId?: string
    previousEmbedContext?: string
    name: string
    uuid: string
    subprocessUuid: string
    breadcrumbs: ProcessInstanceBreadcrumb[]
  }>()

  breadcrumbs: ProcessInstanceBreadcrumb[] = []

  bpmnInstanceActionsStore = inject(BpmnInstanceActionsStore)

  BpmnDiagram = BpmnDiagram

  reactProps = computed<BpmnDiagramProps>(() => ({
    data: this.#canvasData(),
    readonly: true,
    allowReadOnly: false,
    highlightedNodeId: undefined,
    navAreas: [],
    navAreasLaneId: null,
    paletteDragType: null,
    panTarget: null,
    fitViewKey: this.#canvasInstanceUuid(),
    // The details panel covers the bottom 60% of the canvas at its default size.
    bottomPanelHeightPc: BpmnGraphConstants.OptionsPanelHeightPc,
    editingUsersByNode: {},
    aiDraftPreview: null,
    navDisabledNodeIds: [],
    onOpenConfigPanel: this.#onOpenConfigPanel,
    onNodeRightClick: this.#onNodeRightClick,
    onSequenceClick: noop,
    onCanvasInteraction: this.#onCanvasInteraction,
    onViewportChange: this.#onViewportChange,
    onPaneClicked: this.#onPaneClicked,
    // Edit-side callbacks: never invoked in readonly mode (drag/connect/resize
    // are gated by draggable/nodesConnectable=false at the RF layer), but the
    // props contract requires them. Point them at noop.
    onNodesPositionChanged: noop,
    onNodesSwimlaneChanged: noop,
    onNodesDragging: noop,
    onBoundaryEventPositionChanged: noop,
    onSwimlaneRightClick: noop,
    onMultiSelection: noop,
    onMultiSelectionCleared: noop,
    onSwimlaneResize: noop,
    onNavAreaClick: noop,
    onConfirmAreaRemoval: () => Promise.resolve(false),
    onRenameNode: noop,
    onShowSubprocessesMenu: noop,
    onPaletteDrop: noop,
    onNodeDrop: noop,
    onDropError: noop,
    onSequenceDrop: noop,
    onSequenceSegmentMoved: noop,
    onSequencesChanged: noop,
    onSequenceCreate: noop,
    onNodeCreateWithSequence: noop,
    onSwimlaneAdd: noop,
    onSwimlaneMove: noop,
    onSwimlaneDelete: noop,
    onSwimlaneRename: noop,
    onConfirmAutoArrange: () => Promise.resolve(false),
    onPlanAutoLayout: () => Promise.resolve({ nodes: [], swimlanes: [], sequences: [] }),
    onAutoLayoutApplied: () => Promise.resolve(false),
    onLayoutPreviewChange: noop,
  }))

  constructor() {
    this.#applicationProcessData$ = toObservable(this.applicationProcessData)
    // The "Open Subprocess
    // Instance" action fires BPMNNodeActionsEvents.navToEmbeddedInstance, whose
    // sample below funnels the selected node into this handler.
    BPMNNodeActionsEffects.navToEmbeddedInstanceFx.use(this.onNavigateToEmbeddedProcess.bind(this))
  }

  ngOnInit(): void {
    AppEvents.processInstancePageInit()

    this.#store
      .select(selectQueryParams)
      .pipe(
        map((queryParams) => ({
          breadcrumbs: queryParams['breadcrumbs'],
          embedId: queryParams['embedId'],
          embedContext: queryParams['context'],
        })),
        tap(({ breadcrumbs, embedId, embedContext }) => {
          this.breadcrumbs = breadcrumbs ? JSON.parse(breadcrumbs) : []
          this.#embedId = embedId
          this.#embedContext = embedContext
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()

    withRegion(this.#Effector, () => {
      sample({
        clock: BPMNNodeActionsEvents.navToEmbeddedInstance,
        source: { node: BpmnEditorStores.selectedNode$ },
        fn: ({ node }) => ({ node }),
        target: BPMNNodeActionsEffects.navToEmbeddedInstanceFx,
      })
    })

    combineLatest([
      this.#applicationProcessData$,
      this.#processInstanceService.processInstanceLoading,
    ])
      .pipe(
        filter(
          ([applicationProcessData, loading]) =>
            applicationProcessData?.processData?.definition && !loading
        ),
        tap(([applicationProcessData]) => {
          BpmnEditorEvents.processVersionChanged(applicationProcessData.processData.definition.id)
          ProcessEffector.loadProcess(
            applicationProcessData.processData?.definition as ProcessDefinition
          )
          // Feeds BpmnEditorStores.readonly$ — the actions store's option
          // filter (getInstanceActionsByNodeType) consults it via
          // ApplicationDetailsStore.definitionExists.
          BpmnEditorEvents.onCanvasReadonly(true)

          this.#canvasInstanceUuid.set(applicationProcessData.processData?.instance?.uuid ?? null)
          this.#canvasData.set(buildInstanceCanvasData(applicationProcessData) ?? EMPTY_CANVAS_DATA)
        }),
        // takeUntil (not takeUntilDestroyed) so the subscription tears down
        // before processInstanceLoading emits false on instance reset.
        takeUntil(this.#destroy$)
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.#destroy$.next()
    this.#destroy$.complete()
    clearNode(this.#Effector)
    AppEvents.processInstancePageDestroy()
    this.#processInstanceService.resetInstanceData()
  }

  onSubprocessNavigation(subprocessUuid: string, name: string, uuid: string): void {
    const _embedId = this.#embedId ?? null
    const _embedContext = this.#embedContext ?? null

    this.navigateToSubProcess.emit({
      subprocessUuid,
      name,
      uuid,
      ...(_embedId && { previousEmbedId: _embedId }),
      ...(_embedContext && { previousEmbedContext: _embedContext }),
      breadcrumbs: this.breadcrumbs,
    })
  }

  goBackToParent(): void {
    this.backToParentProcess.emit({ breadcrumbs: this.breadcrumbs })
  }

  onNavigateToEmbeddedProcess({ node }: { node: NodeDefinitionNormalized }): void {
    const applicationProcessData = this.applicationProcessData()
    const subprocessUuid = applicationProcessData.processData?.instance?.uuid
    if (!subprocessUuid) {
      return
    }
    const _embedId = this.#embedId ?? null
    const _embedContext = this.#embedContext ?? null

    this.navigateToEmbeddedProcess.emit({
      embedId: node.id,
      context: applicationProcessData?.processData?.instance?.embedContext,
      ...(_embedId && { previousEmbedId: _embedId }),
      ...(_embedContext && { previousEmbedContext: _embedContext }),
      name: applicationProcessData?.processData?.definition.resourceName,
      uuid: subprocessUuid,
      subprocessUuid,
      breadcrumbs: this.breadcrumbs,
    })
  }

  // Selects the node / boundary in the effector stores and shows the floating
  // instance-actions menu at the entity's viewport rect. Same event shape the
  // RF editor's onOpenConfigPanel fires.
  #onOpenConfigPanel: BpmnDiagramProps['onOpenConfigPanel'] = (nodeOrBoundaryEvent, selection) => {
    const clickPayload = {
      mouseEvent: null,
      entity: nodeOrBoundaryEvent,
      viewportCoords: selection.viewportCoords,
      canvasCoords: selection.viewportCoords,
      eventCanvasCoords: { x: selection.viewportCoords.x, y: selection.viewportCoords.y },
    }
    if (selection.type === 'node') {
      BpmnNodeEvents.nodeLeftClick(clickPayload as BpmnMouseEvent<NodeDefinition>)
    } else {
      BpmnBoundaryEventEvents.boundaryLeftClick(
        clickPayload as unknown as BpmnMouseEvent<BoundaryEvent>
      )
    }
  }

  #onNodeRightClick: BpmnDiagramProps['onNodeRightClick'] = (nodeOrBoundaryEvent, selection) => {
    const { x, y } = selection.viewportCoords
    const clickPayload = {
      mouseEvent: { offsetX: x, offsetY: y } as MouseEvent,
      entity: nodeOrBoundaryEvent,
      viewportCoords: selection.viewportCoords,
      canvasCoords: selection.viewportCoords,
      eventCanvasCoords: { x, y },
    }
    if (selection.type === 'node') {
      BpmnNodeEvents.nodeRightClick(clickPayload as BpmnMouseEvent<NodeDefinition>)
    } else {
      BpmnBoundaryEventEvents.boundaryRightClick(
        clickPayload as unknown as BpmnMouseEvent<BoundaryEvent>
      )
    }
  }

  // Hides the floating menu on pan/zoom/pane-click, mirroring the RF editor.
  #onCanvasInteraction = (): void => {
    BpmnNodeEvents.nodeMoving({} as BpmnMouseEvent<NodeDefinition>)
  }

  #onViewportChange: BpmnDiagramProps['onViewportChange'] = ({ zoom }) => {
    if (zoom !== this.#lastReportedZoom) {
      this.#lastReportedZoom = zoom
      BpmnEditorEvents.canvasZoom({ zoom })
    } else {
      BpmnEditorEvents.canvasPan()
    }
  }

  #onPaneClicked: BpmnDiagramProps['onPaneClicked'] = () => {
    BpmnEditorEvents.canvasLeftClick()
  }
}
